import React, {
  Component,
  useState,
  useCallback,
  useRef,
  useEffect,
  useLayoutEffect,
} from "react";
import AddImageModal from "@/features/tutor/components/PaperCamera/AddImageModal";
import { usePaperCamera } from "@/features/tutor/components/PaperCamera/usePaperCamera";
import { AnimatePresence, LazyMotion, domAnimation, m } from "framer-motion";
import { type Editor, type TLEditorSnapshot } from "tldraw";
import { TldrawCanvasAgent } from "@/canvas/TldrawCanvasAgent";
import { TldrawAgentAppContextProvider } from "@/canvas/agent/TldrawAgentAppProvider";
import type { TldrawAgentApp } from "@/canvas/agent/TldrawAgentApp";
import { CanvasVoiceBridge } from "./CanvasVoiceBridge";
import { CanvasConnectionRecovery } from "./CanvasConnectionRecovery";
import { CanvasChatBridge } from "./CanvasChatBridge";
import { NotepadPageNav } from "./NotepadPageNav";
import {
  WhiteboardScan,
  WHITEBOARD_SCAN_EVENT,
} from "@/canvas/components/WhiteboardScan";
import type { ChatHistoryItem } from "@/shared/types/ChatHistoryItem";
import type { Streaming } from "@/shared/types/Streaming";
import type { AgentAction } from "@/shared/types/AgentAction";
import type { CanvasMetricsChunkData } from "@/canvas/agent/chunk_protocol";
import { useWebsocketStore } from "@/features/tutor/store/useWebsocketStore";
import {
  useNotepadStore,
  selectIsAIActiveOnCurrentPage,
  selectIsNotepadNonInteractive,
} from "../store/useNotepadStore";
import { useTldrawDrawLock } from "@/drawlock/useTldrawDrawLock";
import { NOTEPAD_SURFACE_ID } from "@/drawlock/surfaceIds";
import { useTurnStore } from "@/features/tutor/store/useTurnStore";
import { useSessionStore } from "@/features/tutor/store/useSessionStore";
import { useInactivityTimer } from "@/features/tutor/hooks/useInactivityTimer";
import { useRuntimeContext } from "@/features/tutor/context/RuntimeContext";
import { usePlayerTick } from "@/features/tutor/hooks/usePlayerTick";
import { useNotepadInactivityStore } from "../store/useNotepadInactivityStore";
import { SHOW_IMAGE_CAPTURE_DEBUG } from "@/flags";
import { debugLog, debugWarn } from "@/utils/debugLog";

/** Keeps tldraw errors from crashing VoDemoContainer and its WS session.
 *  Auto-resets up to 3 times in 10 s, then shows a "Reopen Canvas" button. */
class CanvasErrorBoundary extends Component<
  { children: React.ReactNode; onRetry?: () => void },
  { hasError: boolean }
> {
  private resetTimestamps: number[] = [];
  private autoResetTimer: ReturnType<typeof setTimeout> | null = null;

  private static MAX_RESETS = 3;
  private static RESET_WINDOW_MS = 10_000;
  private static AUTO_RESET_DELAY_MS = 500;

  constructor(props: { children: React.ReactNode; onRetry?: () => void }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.error("[Canvas] Error:", error);

    // Prune old timestamps outside the window
    const now = Date.now();
    this.resetTimestamps = this.resetTimestamps.filter(
      (t) => now - t < CanvasErrorBoundary.RESET_WINDOW_MS,
    );

    if (this.resetTimestamps.length < CanvasErrorBoundary.MAX_RESETS) {
      this.resetTimestamps.push(now);
      // Auto-recover after a brief delay so tldraw can re-mount cleanly
      this.autoResetTimer = setTimeout(() => {
        this.autoResetTimer = null;
        this.setState({ hasError: false });
        this.props.onRetry?.();
      }, CanvasErrorBoundary.AUTO_RESET_DELAY_MS);
    }
    // else: too many resets in quick succession — stay in error state
  }

  componentWillUnmount() {
    if (this.autoResetTimer) {
      clearTimeout(this.autoResetTimer);
      this.autoResetTimer = null;
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center w-full h-full gap-4 text-gray-600">
          <p className="text-sm font-medium">
            The canvas encountered an error.
          </p>
          <button
            onClick={() => {
              this.resetTimestamps = [];
              this.setState({ hasError: false });
              this.props.onRetry?.();
            }}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Reopen Canvas
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export interface TldrawCanvasPanelProps {
  isOpen: boolean;
  slideKey: number | string | null;
  canvasSessionId: string | null;
  onStreamComplete?: () => void;
  onError?: (error: Error) => void;
  onActionExecuted?: (action: any) => void;
  onCanvasCloseChatHistory?: (chatHistory: CanvasCloseHistoryEntry[]) => void;
  /** Receives a getter for the close history, read synchronously on page unload. */
  onRegisterHistoryGetter?: (getter: () => CanvasCloseHistoryEntry[]) => void;
  onClose?: () => void;
  onSharePanelRef?: (el: HTMLDivElement | null) => void;
  blobScale?: number;
  onViewportChanged?: (hasChanged: boolean) => void;
}

interface SavedCanvasState {
  snapshot: TLEditorSnapshot | null;
  chatHistory: ChatHistoryItem[];
  slideKey: number | string | null;
}

export interface CanvasCloseHistoryEntry {
  type: "prompt" | "action";
  promptSource?: string;
  userFacingMessage?: string | null;
  agentFacingMessage?: string | null;
  actionType?: string;
  text?: string;
  complete?: boolean;
}

const MAX_CANVAS_CLOSE_HISTORY_ITEMS = 120;
const MAX_CANVAS_CLOSE_SNIPPET_CHARS = 240;

function isEditorCrashed(editor: Editor): boolean {
  // getCrashingError is tldraw-internal.
  return Boolean(
    (
      editor as unknown as { getCrashingError?: () => unknown }
    ).getCrashingError?.(),
  );
}

function normalizeCloseHistorySnippet(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

function clampCloseHistorySnippet(text: string): string {
  if (text.length <= MAX_CANVAS_CLOSE_SNIPPET_CHARS) return text;
  return `${text.slice(0, MAX_CANVAS_CLOSE_SNIPPET_CHARS - 3)}...`;
}

function buildCanvasCloseHistory(
  history: ChatHistoryItem[],
): CanvasCloseHistoryEntry[] {
  if (!Array.isArray(history) || history.length === 0) return [];

  // Keep the newest history first when applying cutoff, then restore order.
  const newestFirst = history.slice().reverse();
  const entriesNewestFirst: CanvasCloseHistoryEntry[] = [];

  for (const item of newestFirst) {
    if (entriesNewestFirst.length >= MAX_CANVAS_CLOSE_HISTORY_ITEMS) break;

    if (item.type === "prompt") {
      const userFacingMessage =
        typeof item.userFacingMessage === "string"
          ? clampCloseHistorySnippet(
              normalizeCloseHistorySnippet(item.userFacingMessage),
            )
          : item.userFacingMessage;
      const agentFacingMessage =
        typeof item.agentFacingMessage === "string"
          ? clampCloseHistorySnippet(
              normalizeCloseHistorySnippet(item.agentFacingMessage),
            )
          : item.agentFacingMessage;

      if (!userFacingMessage && !agentFacingMessage) continue;

      entriesNewestFirst.push({
        type: "prompt",
        promptSource: item.promptSource,
        userFacingMessage,
        agentFacingMessage,
      });
      continue;
    }

    if (item.type === "action") {
      const action = item.action as Streaming<AgentAction> & {
        _type?: string;
        text?: string;
      };

      const actionType =
        typeof action._type === "string" && action._type.length > 0
          ? action._type
          : undefined;
      const text =
        typeof action.text === "string" && action.text.trim().length > 0
          ? clampCloseHistorySnippet(normalizeCloseHistorySnippet(action.text))
          : undefined;

      if (!actionType && !text) continue;

      entriesNewestFirst.push({
        type: "action",
        actionType,
        text,
        complete: action.complete,
      });
    }
  }

  return entriesNewestFirst.reverse();
}

const CanvasPresence = ({ children }: React.PropsWithChildren) => (
  <LazyMotion features={domAnimation}>
    <AnimatePresence>{children}</AnimatePresence>
  </LazyMotion>
);

/** Full-screen notepad overlay: one canvas per page key, with its saved board. */
export function TldrawCanvasPanel({
  isOpen,
  slideKey,
  canvasSessionId,
  onStreamComplete,
  onError,
  onActionExecuted,
  onCanvasCloseChatHistory,
  onRegisterHistoryGetter,
  onClose,
  onSharePanelRef,
  blobScale,
  onViewportChanged,
}: TldrawCanvasPanelProps) {
  const currentPageIndex = useNotepadStore((s) => s.currentPageIndex);
  const isLimitedMode = useNotepadStore((s) => s.isLimitedMode);
  const isAIActive = useNotepadStore(selectIsAIActiveOnCurrentPage);
  const isNotepadBite = useNotepadStore((s) => s.isNotepadBite);

  // Per-page storage key: in unlimited mode, scope each page separately
  const pageKey =
    !isLimitedMode && currentPageIndex >= 0
      ? `${slideKey}-page-${currentPageIndex}`
      : slideKey;

  const { resetAndRestartInactivityTimer } = useInactivityTimer();
  const { emit: emitRuntime, pcmPlayerRef } = useRuntimeContext();
  const { enqueue: enqueueClose, cancelPending: cancelClose } =
    usePlayerTick(pcmPlayerRef);

  // Stable ref for the inactivity reset fn so it doesn't destabilise handleMount
  // (useInactivityTimer returns a new function reference every render).
  const resetInactivityRef = useRef(resetAndRestartInactivityTimer);
  resetInactivityRef.current = resetAndRestartInactivityTimer;

  // Resets the backend's notepad inactivity window; goes over the tutor WS, not the gateway.
  const emitNotepadActivityRef = useRef(() => emitRuntime("NOTEPAD_ACTIVITY"));
  emitNotepadActivityRef.current = () => emitRuntime("NOTEPAD_ACTIVITY");

  const [app, setApp] = useState<TldrawAgentApp | null>(null);
  const [canvasResetVersion, setCanvasResetVersion] = useState(0);
  const settledTurnIdsRef = useRef<Set<string>>(new Set());

  // Page keys already greeted; here so it outlives the bridge's per-page remount.
  const greetedPagesRef = useRef<Set<string>>(new Set());

  // Ref mirror of app state for synchronous access during unmount
  const appRef = useRef<TldrawAgentApp | null>(null);

  // Raw tldraw Editor ref — survives TldrawAgentApp.dispose() which nulls _editor
  const editorRef = useRef<Editor | null>(null);

  // Space bar locks the pen down so the student can write on a trackpad.
  // Registered per mount, and stood down whenever the notepad is showing
  // history or the AI is drawing on it.
  const isNotepadNonInteractive = useNotepadStore(
    selectIsNotepadNonInteractive,
  );
  const getDrawLockEditor = useCallback(() => editorRef.current, []);
  useTldrawDrawLock({
    surfaceId: NOTEPAD_SURFACE_ID,
    getEditor: getDrawLockEditor,
    isActive: !isNotepadNonInteractive,
  });

  // Last debounced board capture; the fallback when the editor can't be read.
  const snapshotRef = useRef<TLEditorSnapshot | null>(null);

  const unsubscribeSnapshotRef = useRef<(() => void) | null>(null);

  const snapshotDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // Chat history mirror, still readable after the agent app is disposed.
  const chatHistoryRef = useRef<ChatHistoryItem[]>([]);

  // Saved boards by page key; survives a failed sessionStorage write.
  const boardsRef = useRef(new Map<string, SavedCanvasState>());

  const readSavedState = useCallback(
    (key: typeof pageKey): SavedCanvasState | null => {
      if (key === null || key === "") return null;
      const saved = boardsRef.current.get(String(key));
      if (saved) return saved;
      try {
        const raw = sessionStorage.getItem(`canvas_state_${key}`);
        return raw ? (JSON.parse(raw) as SavedCanvasState) : null;
      } catch {
        return null;
      }
    },
    [],
  );

  // Tracks whether a stream is currently in progress — used to fire onStreamComplete on mid-stream close
  const isStreamingRef = useRef<boolean>(false);
  const lastSentHistorySignatureRef = useRef<string>("");

  // Debounce guard for inactivity reset — at most once per second
  const lastInactivityResetRef = useRef<number>(0);

  // By reference: both halves are fresh objects whenever anything changed.
  const lastWrittenRef = useRef<{
    key: typeof pageKey;
    snapshot: TLEditorSnapshot | null;
    chatHistory: ChatHistoryItem[];
  } | null>(null);

  const writeCanvasState = useCallback(
    (
      key: NonNullable<typeof pageKey>,
      snapshot: TLEditorSnapshot | null,
      chatHistory: ChatHistoryItem[],
    ) => {
      const last = lastWrittenRef.current;
      if (
        last &&
        last.key === key &&
        last.snapshot === snapshot &&
        last.chatHistory === chatHistory
      ) {
        return;
      }
      const state: SavedCanvasState = { snapshot, chatHistory, slideKey: key };
      boardsRef.current.set(String(key), state);
      lastWrittenRef.current = { key, snapshot, chatHistory };
      try {
        sessionStorage.setItem(`canvas_state_${key}`, JSON.stringify(state));
      } catch {
        // sessionStorage may be full or unavailable — best-effort
      }
    },
    [],
  );

  const saveBoard = useCallback(
    (key: NonNullable<typeof pageKey>, editor: Editor | null) => {
      let snapshot = snapshotRef.current;
      try {
        if (editor) snapshot = editor.getSnapshot();
      } catch {
        // use the last debounced snapshot
      }
      if (snapshot === null) return;
      let chatHistory = chatHistoryRef.current;
      try {
        const agent = appRef.current?.agents?.getAgent();
        if (agent) chatHistory = agent.chat.getHistory();
      } catch {
        // use ref mirror
      }
      writeCanvasState(key, snapshot, [...chatHistory]);
    },
    [writeCanvasState],
  );

  // The mounted canvas's own key, so saves never land on the page being rendered.
  const mountedPageKeyRef = useRef<typeof pageKey>(pageKey);

  // Every writer must respect a restart until the discarded editor is removed.
  const discardOnUnmountRef = useRef(false);
  // Counts a crashed canvas too: its agent app is gone, its editor is not.
  const mountedCanvasCountRef = useRef(0);

  // Register a clearPersistedState callback so VoDemoContainer can wipe
  // in-memory refs on restart (sessionStorage alone is insufficient).
  useEffect(() => {
    const clearState = () => {
      boardsRef.current.clear();
      lastWrittenRef.current = null;
      snapshotRef.current = null;
      chatHistoryRef.current = [];
      lastSentHistorySignatureRef.current = "";
      settledTurnIdsRef.current.clear();
      // Unconsumed, the flag would eat the next close's save.
      if (mountedCanvasCountRef.current > 0) discardOnUnmountRef.current = true;
      // Reopening during AnimatePresence exit can keep the same page mounted.
      setCanvasResetVersion((version) => version + 1);
    };
    useNotepadStore.getState().registerClearPersistedState(clearState);
    return () => {
      useNotepadStore.getState().unregisterClearPersistedState();
    };
  }, []);

  // Called by switchToPage and close() before the canvas goes.
  const flushCanvasState = useCallback(() => {
    const key = mountedPageKeyRef.current;
    const editor = editorRef.current;
    if (
      key === null ||
      key === "" ||
      !editor ||
      editor.isDisposed ||
      isEditorCrashed(editor) ||
      discardOnUnmountRef.current
    )
      return;
    saveBoard(key, editor);
  }, [saveBoard]);

  useEffect(() => {
    useNotepadStore.getState().registerFlushCanvasState(flushCanvasState);
    window.addEventListener("pagehide", flushCanvasState);
    return () => {
      window.removeEventListener("pagehide", flushCanvasState);
      useNotepadStore.getState().unregisterFlushCanvasState();
    };
  }, [flushCanvasState]);

  // Not during render: a discarded transition render must not reset these.
  const prevPageKeyRef = useRef(pageKey);
  useLayoutEffect(() => {
    if (prevPageKeyRef.current === pageKey) return;
    prevPageKeyRef.current = pageKey;
    snapshotRef.current = null;
    chatHistoryRef.current = [];
    lastSentHistorySignatureRef.current = "";
    settledTurnIdsRef.current.clear();
  }, [pageKey]);

  const settleAgentAfterTurn = useCallback(
    (turnId: string) => {
      if (!turnId || settledTurnIdsRef.current.has(turnId)) {
        return;
      }

      const activeTurnId = useTurnStore.getState().getTurnId();
      if (activeTurnId && activeTurnId !== turnId) {
        return;
      }

      settledTurnIdsRef.current.add(turnId);

      if (isStreamingRef.current) {
        isStreamingRef.current = false;
        const agent = appRef.current?.agents.getAgent();
        if (agent) {
          chatHistoryRef.current = agent.chat.getHistory();
        }
        onStreamComplete?.();
      }

      const agent = appRef.current?.agents.getAgent();
      if (!agent) {
        return;
      }

      try {
        agent.cursor.hide();
        agent.requests.clearActiveRequest();
        agent.requests.setCancelFn(null);
        agent.viewportFollow.onRequestEnd();
        if (agent.mode.getCurrentModeType() !== "idling") {
          agent.mode.setMode("idling");
        }
      } catch (error) {
        debugWarn(
          "[TldrawCanvasPanel] Failed to settle canvas agent after turn:",
          error,
        );
      }

      // Persist after each turn so state survives a mid-session refresh.
      const settlePageKey = mountedPageKeyRef.current;
      if (
        settlePageKey &&
        snapshotRef.current &&
        !discardOnUnmountRef.current
      ) {
        const agentForSave = appRef.current?.agents.getAgent();
        writeCanvasState(
          settlePageKey,
          snapshotRef.current,
          agentForSave
            ? agentForSave.chat.getHistory()
            : chatHistoryRef.current,
        );
      }
    },
    [onStreamComplete, writeCanvasState],
  );

  useEffect(() => {
    if (!isOpen) return;

    const handleMetrics = (event: Event) => {
      const customEvent = event as CustomEvent<{
        turnId: string;
        metrics: CanvasMetricsChunkData;
      }>;
      const detail = customEvent.detail;
      if (!detail?.turnId || !detail.metrics) return;

      if (
        detail.metrics.stage === "done" ||
        detail.metrics.stage === "cancel"
      ) {
        settleAgentAfterTurn(detail.turnId);
      }
    };

    window.addEventListener("canvas:metrics", handleMetrics);
    return () => window.removeEventListener("canvas:metrics", handleMetrics);
  }, [isOpen, settleAgentAfterTurn]);

  useEffect(() => {
    if (!isOpen) return;

    const handleTurnDone = (event: Event) => {
      const customEvent = event as CustomEvent<{ turn_id?: string }>;
      const turnId = customEvent.detail?.turn_id;
      if (!turnId) return;
      settleAgentAfterTurn(turnId);
    };

    window.addEventListener("canvas:turn-done", handleTurnDone);
    return () => window.removeEventListener("canvas:turn-done", handleTurnDone);
  }, [isOpen, settleAgentAfterTurn]);

  // --- Error banner state and logic ---
  const [bannerMessage, setBannerMessage] = useState<string | null>(null);
  const bannerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showBanner = useCallback((message: string) => {
    // Clear any existing auto-dismiss timer before setting a new one
    if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
    setBannerMessage(message);
    // Auto-dismiss after 5 seconds (locked decision: banner clears itself)
    bannerTimerRef.current = setTimeout(() => {
      setBannerMessage(null);
      bannerTimerRef.current = null;
    }, 5000);
  }, []);

  const dismissBanner = useCallback(() => {
    if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
    bannerTimerRef.current = null;
    setBannerMessage(null);
  }, []);

  // Cleanup banner timer on unmount
  useEffect(() => {
    return () => {
      if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
    };
  }, []);

  // True once the canvas gateway WS has opened (used for connecting overlay).
  // In student review mode or on non-latest pages there is no WS — treat as always connected.
  const notepadWs = useNotepadStore((state) => state.notepadWs);
  const ingressType = useNotepadStore((state) => state.ingressType);
  const isGatewayConnected =
    ingressType === "student" ||
    (notepadWs !== null && notepadWs.readyState === WebSocket.OPEN);

  // A bite's canvas shows under the Start Learning modal (ENGG-2434) before any
  // session exists. Latched: sessionId returns to null mid-lesson (WS loss,
  // restart), and a live value would shield a canvas the student is writing on.
  const hasSession = useSessionStore((state) => state.sessionId !== null);
  const hasEverHadSession = useRef(hasSession);
  if (hasSession) hasEverHadSession.current = true;
  const isPreSession = !hasEverHadSession.current;

  // A bite's canvas is the lesson surface and reconnects on every slide; the
  // "Connection lost" banner reports real outages without blanking it.
  const suppressConnectingOverlay = isNotepadBite || isPreSession;

  // Canvas inactivity timer — only for student-initiated canvas (Mode 1).
  // Use getState() directly to avoid unstable Zustand function references in deps.
  const notepadInactivityResetRef = useRef(() =>
    useNotepadInactivityStore.getState().reset(),
  );
  useEffect(() => {
    if (isOpen && ingressType === "student") {
      useNotepadInactivityStore.getState().start();
    } else {
      useNotepadInactivityStore.getState().stop();
    }
    return () => {
      useNotepadInactivityStore.getState().stop();
    };
  }, [isOpen, ingressType]);

  // Show banner when the gateway is restarting (server_shutdown message)
  useEffect(() => {
    if (!isOpen) return;
    const handler = () => {
      showBanner("Canvas is reconnecting after a server restart…");
    };
    window.addEventListener("canvas:server-shutdown", handler);
    return () => window.removeEventListener("canvas:server-shutdown", handler);
  }, [isOpen, showBanner]);

  // Trigger banner on WS disconnect while canvas is open
  const wsConnected = useWebsocketStore((state: any) => state.wsConnected);
  const prevWsRef = useRef<boolean>(wsConnected);
  useEffect(() => {
    const prev = prevWsRef.current;
    prevWsRef.current = wsConnected;
    // Only trigger on true→false transition (not on initial render where wsConnected may be false)
    if (prev && !wsConnected && isOpen) {
      showBanner("Connection lost. Canvas will resume when reconnected.");
    }
  }, [wsConnected, isOpen, showBanner]);

  // The agent's "close" action: start the countdown at its audio timestamp, not on receipt.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const timestamp =
        typeof detail?.timestamp === "number" ? detail.timestamp : 0;
      debugLog("[TldrawCanvasPanel] notepad:close event received", {
        timestamp,
        detail,
      });

      const targetMs = (window.__canvasPlayedMsAtStart ?? 0) + timestamp;
      debugLog("[TldrawCanvasPanel] scheduling close countdown", {
        targetMs,
        timestamp,
      });
      cancelClose();
      if (timestamp > 0) {
        enqueueClose(targetMs, () => {
          useNotepadStore.getState().startCloseCountdown();
        });
      } else {
        useNotepadStore.getState().startCloseCountdown();
      }
    };
    window.addEventListener("notepad:close", handler);
    return () => {
      window.removeEventListener("notepad:close", handler);
      cancelClose();
    };
  }, [isOpen, enqueueClose, cancelClose]);

  // --- Handlers ---

  const handleMount = useCallback(
    (instance: TldrawAgentApp) => {
      appRef.current = instance;
      editorRef.current = instance.editor;
      setApp(instance);
      isStreamingRef.current = false; // reset streaming state on each mount
      chatHistoryRef.current = []; // reset chat mirror on each mount
      snapshotRef.current = null; // reset snapshot on each mount

      onRegisterHistoryGetter?.(() =>
        buildCanvasCloseHistory(chatHistoryRef.current),
      );

      const captureSnapshotNow = () => {
        try {
          if (instance.isDisposed || discardOnUnmountRef.current) return;
          snapshotRef.current = instance.editor.getSnapshot();
        } catch (error) {
          debugWarn(
            "[TldrawCanvasPanel] Failed to capture editor snapshot:",
            error,
          );
        }
      };

      const onStoreChange = () => {
        // getSnapshot() serializes the whole board; drawing fires dozens of changes a second.
        if (snapshotDebounceTimerRef.current) {
          clearTimeout(snapshotDebounceTimerRef.current);
        }
        snapshotDebounceTimerRef.current = setTimeout(() => {
          snapshotDebounceTimerRef.current = null;
          captureSnapshotNow();
        }, 500);

        // Debounced inactivity reset — at most once per second, skip during agent stream
        const now = Date.now();
        if (
          !isStreamingRef.current &&
          now - lastInactivityResetRef.current >= 1000
        ) {
          lastInactivityResetRef.current = now;
          resetInactivityRef.current();
          notepadInactivityResetRef.current();
          emitNotepadActivityRef.current();
        }
      };
      captureSnapshotNow(); // capture initial snapshot (no debounce)
      unsubscribeSnapshotRef.current =
        instance.editor.store.listen(onStoreChange);

      // handleEditorMount (layout phase, same commit) set the key and loaded shapes.
      const saved = readSavedState(mountedPageKeyRef.current);
      if (saved) {
        const agent = instance.agents.getAgent();
        if (agent && saved.chatHistory.length > 0) {
          agent.chat.setHistory(saved.chatHistory);
          chatHistoryRef.current = saved.chatHistory; // sync mirror with restored history
        }
      }
    },
    [onRegisterHistoryGetter, readSavedState],
  );

  const handleEditorMount = useCallback(
    (key: typeof pageKey, editor: Editor) => {
      mountedPageKeyRef.current = key;
      editorRef.current = editor;
      mountedCanvasCountRef.current += 1;
      const saved = readSavedState(key);
      if (!saved?.snapshot) return;
      try {
        editor.loadSnapshot(saved.snapshot);
      } catch (error) {
        debugWarn("[TldrawCanvasPanel] Failed to load saved board:", error);
      }
    },
    [readSavedState],
  );

  const handleBeforeCanvasUnmount = useCallback(
    (key: typeof pageKey, editor: Editor) => {
      mountedCanvasCountRef.current = Math.max(
        0,
        mountedCanvasCountRef.current - 1,
      );
      if (discardOnUnmountRef.current) {
        discardOnUnmountRef.current = false;
        if (editorRef.current === editor) editorRef.current = null;
        return;
      }
      if (editorRef.current === editor) editorRef.current = null;
      if (
        key === null ||
        key === "" ||
        editor.isDisposed ||
        isEditorCrashed(editor)
      )
        return;
      // The agent app is still alive: its provider tears down in the passive phase.
      saveBoard(key, editor);
    },
    [saveBoard],
  );

  const handleUnmount = useCallback(() => {
    unsubscribeSnapshotRef.current?.();
    unsubscribeSnapshotRef.current = null;

    if (snapshotDebounceTimerRef.current) {
      clearTimeout(snapshotDebounceTimerRef.current);
      snapshotDebounceTimerRef.current = null;
    }

    const closeHistory = buildCanvasCloseHistory(chatHistoryRef.current);
    if (closeHistory.length > 0) {
      const signature = JSON.stringify(closeHistory);
      if (signature !== lastSentHistorySignatureRef.current) {
        lastSentHistorySignatureRef.current = signature;
        onCanvasCloseChatHistory?.(closeHistory);
      }
    }
    if (isStreamingRef.current) {
      isStreamingRef.current = false;
      onStreamComplete?.();
    }
    appRef.current = null;
    setApp(null);
  }, [onCanvasCloseChatHistory, onStreamComplete]);

  // Tracks streaming state for mid-stream close detection
  const handleActionExecuted = useCallback(
    (action: Streaming<AgentAction>) => {
      // Detect stream start (first incomplete action when not already streaming)
      if (!action.complete && !isStreamingRef.current) {
        isStreamingRef.current = true;
      } else if (!action.complete) {
        isStreamingRef.current = true;
      }
      // Keeps the mirror current for a mid-stream close; getHistory is an atom read.
      const agent = appRef.current?.agents.getAgent();
      if (agent) {
        chatHistoryRef.current = agent.chat.getHistory();
      }
      onActionExecuted?.(action); // forward to parent
    },
    [onActionExecuted],
  );

  // Clears streaming state when stream ends normally; snapshots chat history while app is still alive
  const handleStreamComplete = useCallback(() => {
    isStreamingRef.current = false;
    // Snapshot chat history now — while app is still alive and agent.chat is accessible.
    const agent = appRef.current?.agents.getAgent();
    if (agent) {
      chatHistoryRef.current = agent.chat.getHistory();
    }
    onStreamComplete?.();
  }, [onStreamComplete]);

  // onError wrapper — shows banner for CANVAS_ERROR and other canvas errors
  const handleError = useCallback(
    (err: Error) => {
      showBanner("Canvas encountered an error. Please try again.");
      onError?.(err); // forward to parent
    },
    [showBanner, onError],
  );

  // Fallback for malformed actions: restore the last saved board
  const handleActionCrash = useCallback(() => {
    if (discardOnUnmountRef.current) return;
    try {
      const saved = readSavedState(mountedPageKeyRef.current);
      if (saved?.snapshot && editorRef.current) {
        debugWarn(
          "[TldrawCanvasPanel] Restoring the saved board after action crash",
        );
        editorRef.current.loadSnapshot(saved.snapshot);
      }
    } catch {
      debugWarn(
        "[TldrawCanvasPanel] Failed to restore snapshot after action crash",
      );
    }
  }, [readSavedState]);

  const tldrawWrapperRef = useRef<HTMLDivElement | null>(null);

  // iPad Safari ignores touch-action:none under a scrollable ancestor. Only on
  // .tl-canvas: preventDefault elsewhere breaks synthetic clicks.
  useEffect(() => {
    const el = tldrawWrapperRef.current;
    if (!el) return;
    const prevent = (e: TouchEvent) => {
      if (!(e.target as Element | null)?.closest(".tl-canvas")) return;
      if (e.cancelable) e.preventDefault();
    };
    el.addEventListener("touchstart", prevent, { passive: false });
    el.addEventListener("touchmove", prevent, { passive: false });
    return () => {
      el.removeEventListener("touchstart", prevent);
      el.removeEventListener("touchmove", prevent);
    };
  }, []);

  // --- Add image to notepad ---
  const [addImageModalOpen, setAddImageModalOpen] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState<
    { id: string; dataUrl: string }[]
  >([]);

  const {
    status: cameraStatus,
    countdown,
    autoCaptureCountdown,
    detectionScore,
    debugValues,
    lastCapturedImage,
    videoRef,
    canvasRef: cameraCanvasRef,
    edgeCanvasRef,
    params,
    setParams,
    open: openCamera,
    close: closeCamera,
    captureManually,
  } = usePaperCamera({ debugMode: SHOW_IMAGE_CAPTURE_DEBUG });

  const handleCameraClick = useCallback(() => {
    setAddImageModalOpen(true);
  }, []);

  const handleImageConfirm = useCallback((b64: string) => {
    const dataUrl = `data:image/jpeg;base64,${b64}`;
    const id =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const agent = appRef.current?.agents.getAgent();
    if (agent) {
      // Sweep only if the photo went out; otherwise nothing is read until the next turn.
      if (agent.submitPageCapture(dataUrl, id)) {
        window.dispatchEvent(new CustomEvent(WHITEBOARD_SCAN_EVENT));
      }
      // Still staged: vision sees what line OCR can't (diagrams, crossings-out).
      agent.queueImageMessages([dataUrl]);
    }
    setPendingAttachments((prev) => [...prev, { id, dataUrl }]);
  }, []);

  // Un-stages the photo only; its digitized lines are the student's and erase like their ink.
  const handleRemoveAttachment = useCallback((id: string, dataUrl: string) => {
    const agent = appRef.current?.agents.getAgent();
    if (agent) agent.dequeueImageMessage(dataUrl);
    setPendingAttachments((prev) => prev.filter((a) => a.id !== id));
  }, []);

  return (
    <CanvasPresence>
      {isOpen && (
        <m.div
          key="canvas-panel"
          initial={{ opacity: 0, scale: 1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          // Absolute overlay — positions relative to the `relative m-[12px]` container in Screen.jsx.
          // It fills that container edge to edge; the caption box keeps clear of
          // the paper via CAPTION_EDGE_GAP in Captions.jsx, not via padding here.
          className={`absolute inset-0 ${isNotepadBite ? "z-40" : "z-[200]"} pointer-events-none flex justify-center`}
        >
          <div className="pointer-events-auto relative w-full h-full">
            {/* Panel container */}
            <div className="w-full h-full bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
              {/* CanvasErrorBoundary contains any Tldraw cleanup/render errors so they
                cannot propagate up and crash VoDemoContainer + the WS session. */}
              <div className="flex-1 min-h-0 relative overflow-hidden">
                {/* Green "scanning your work" sweep, fired on submit. Sits above
                    the canvas and clips to this overflow-hidden container. */}
                <WhiteboardScan />
                {/* Page navigation dropdown — pinned in the top-left margin, to the
                    left of the red rule line (page x=MARGIN_X=80 at the locked z=1
                    camera, so it maps to ~80px from the panel's left edge). */}
                <div className="absolute top-[12px] left-[12px] z-[210] pointer-events-auto">
                  <NotepadPageNav />
                </div>
                <CanvasConnectionRecovery />
                <CanvasErrorBoundary>
                  {/* Error banner — absolute top of panel, pointer-events-auto for dismiss button */}
                  <AnimatePresence>
                    {bannerMessage && (
                      <m.div
                        key="canvas-error-banner"
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-3 inset-x-3 z-[300] flex justify-center pointer-events-none"
                      >
                        <div className="bg-amber-50 border border-amber-300 text-amber-900 text-sm font-medium px-4 py-2 rounded-lg shadow flex items-center gap-3 pointer-events-auto max-w-[90%]">
                          <span>{bannerMessage}</span>
                          <button
                            onClick={dismissBanner}
                            className="ml-auto text-amber-700 hover:text-amber-900 font-bold text-base leading-none"
                            aria-label="Dismiss"
                          >
                            ✕
                          </button>
                        </div>
                      </m.div>
                    )}
                  </AnimatePresence>

                  <div className="w-full h-full relative">
                    <div
                      ref={tldrawWrapperRef}
                      style={{ width: "100%", height: "100%" }}
                    >
                      <TldrawCanvasAgent
                        key={`${pageKey}:${canvasResetVersion}`}
                        onMount={handleMount}
                        onUnmount={handleUnmount}
                        onStreamComplete={handleStreamComplete}
                        onError={handleError}
                        onActionExecuted={handleActionExecuted}
                        onActionCrash={handleActionCrash}
                        pageKey={pageKey}
                        onEditorMount={handleEditorMount}
                        onBeforeUnmount={handleBeforeCanvasUnmount}
                        onSharePanelRef={onSharePanelRef}
                        blobScale={blobScale}
                        onCameraClick={handleCameraClick}
                      />
                    </div>

                    {/* Pre-session shield, not isReadonly: that also blocks the seed. */}
                    {isPreSession && (
                      <div
                        aria-hidden
                        className="absolute inset-0 z-[240]"
                        style={{ cursor: "default", touchAction: "auto" }}
                        onPointerDownCapture={(e) => e.stopPropagation()}
                        onWheelCapture={(e) => e.stopPropagation()}
                      />
                    )}

                    {/* Connecting overlay — reconnect feedback only; see
                        suppressConnectingOverlay. */}
                    <AnimatePresence>
                      {!isGatewayConnected && !suppressConnectingOverlay && (
                        <m.div
                          key="canvas-connecting"
                          initial={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="absolute inset-0 z-[250] flex flex-col items-center justify-center bg-white rounded-2xl gap-3"
                        >
                          <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                          <span className="text-sm text-gray-500 font-medium">
                            Connecting to canvas…
                          </span>
                        </m.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Voice and chat bridges — mounted outside Tldraw DOM after onMount fires (app is non-null). */}
                  {app && (
                    <TldrawAgentAppContextProvider app={app}>
                      <CanvasVoiceBridge
                        canvasSessionId={canvasSessionId}
                        isCurrentCanvas={
                          !app.isDisposed &&
                          mountedPageKeyRef.current === pageKey
                        }
                        greetedPagesRef={greetedPagesRef}
                        pageKey={pageKey}
                        onViewportChanged={onViewportChanged}
                      />
                      <CanvasChatBridge />
                    </TldrawAgentAppContextProvider>
                  )}

                  {/* Always-mounted camera canvas — must stay in DOM so usePaperCamera detection loop works */}
                  <canvas ref={cameraCanvasRef} style={{ display: "none" }} />

                  <AddImageModal
                    open={addImageModalOpen}
                    onClose={() => setAddImageModalOpen(false)}
                    cameraStatus={cameraStatus}
                    countdown={countdown}
                    autoCaptureCountdown={autoCaptureCountdown}
                    detectionScore={detectionScore}
                    debugValues={debugValues}
                    lastCapturedImage={lastCapturedImage}
                    videoRef={videoRef}
                    edgeCanvasRef={edgeCanvasRef}
                    params={params}
                    setParams={setParams}
                    openCamera={openCamera}
                    closeCamera={closeCamera}
                    captureManually={captureManually}
                    onConfirm={handleImageConfirm}
                    debugMode={SHOW_IMAGE_CAPTURE_DEBUG}
                    title="Add image to notepad"
                    attachments={pendingAttachments}
                    onRemoveAttachment={handleRemoveAttachment}
                  />
                </CanvasErrorBoundary>
              </div>
            </div>
            {/* panel container */}
          </div>
          {/* relative wrapper */}
        </m.div>
      )}
    </CanvasPresence>
  );
}
